import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MediaQuery from "react-responsive";
import Expander from "../../../../../components/Expander";
import Citations from "../../../../../components/Citations";
import PublicationLinks from "../../../../../components/Publications/components/PublicationLinks";
import withErrorGuard from "../../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../../components/atoms/ComponentPanel/ComponentPanel";
import Summary from "./Summary";
import Charts from "./Charts";
import {
  getTp53SomaticSchema,
  getTp53SomaticItems,
} from "../../../../../state/selectors/Tp53.selectors";
import HelperText from "../../../../../components/atoms/HelperText";

const IARCTP53Somatic = ({ schema, values }) => {
  const title = "IARC TP53 Somatic";
  const url = "https://tp53.isb-cgc.org/";
  let tmpl = null;
  let version = null;

  if (schema && values) {
    tmpl = values.map((item, i) => {
      let labels;
      if (item.items) {
        // eslint-disable-next-line react/prop-types
        labels = schema.items.properties.items.items.properties;
      }
      version = item.version;
      return (
        <div key={i}>
          <div className="varsome-inner-container">
            <MediaQuery minWidth={768}>
              {item.items ? (
                <table className="varsome-table">
                  <thead>
                    <tr>
                      <th>{labels.age.label}</th>
                      <th>{labels.country.label}</th>
                      <th>{labels.morphology.label}</th>
                      <th>{labels.mut_rate.label}</th>
                      <th>{labels.sample_source.label}</th>
                      <th>{labels.stage.label}</th>
                      <th>{labels.structural_motif.label}</th>
                      <th>{labels.topography.label}</th>
                      <th>{labels.pub_med_references.label}</th>
                    </tr>
                  </thead>
                  <Expander showMore="4" component="tbody">
                    {item.items.map((entry, idx) => {
                      return (
                        <tr key={`${entry}-${idx}`}>
                          <td>{entry.age || "-"}</td>
                          <td>{entry.country || "-"}</td>
                          <td>{entry.morphology || "-"}</td>
                          <td>{entry.mut_rate || "-"}</td>
                          <td>{entry.sample_source || "-"}</td>
                          <td>{entry.stage || "-"}</td>
                          <td>{entry.structural_motif || "-"}</td>
                          <td>{entry.topography || "-"}</td>
                          <td>
                            <PublicationLinks ids={item.pub_med_references} />
                          </td>
                        </tr>
                      );
                    })}
                  </Expander>
                </table>
              ) : null}
            </MediaQuery>
            <MediaQuery maxWidth={767}>
              {item.items ? (
                <Expander showMore="4">
                  {item.items.map((obj, index) => {
                    return (
                      <dl
                        className="dl-horizontal varsome-dl-horizontal"
                        key={`${obj}-${index}`}
                      >
                        <dt className="col-3 col-xs-3">{labels.age.label}</dt>
                        <dd className="col-9 col-xs-9">{obj.age || "-"}</dd>
                        <dt className="col-3 col-xs-3">
                          {labels.country.label}
                        </dt>
                        <dd className="col-9 col-xs-9">{obj.country || "-"}</dd>
                        <dt className="col-3 col-xs-3">
                          {labels.morphology.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {obj.morphology || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.mut_rate.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {obj.mut_rate || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.sample_source.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {obj.sample_source || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">{labels.stage.label}</dt>
                        <dd className="col-9 col-xs-9">{obj.stage || "-"}</dd>
                        <dt className="col-3 col-xs-3">
                          {labels.structural_motif.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {obj.structural_motif || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.topography.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          {obj.topography || "-"}
                        </dd>
                        <dt className="col-3 col-xs-3">
                          {labels.pub_med_references.label}
                        </dt>
                        <dd className="col-9 col-xs-9">
                          <PublicationLinks ids={obj.pub_med_references} />
                        </dd>
                      </dl>
                    );
                  })}
                </Expander>
              ) : null}
            </MediaQuery>
            {item.items && (
              <Citations>
                <p>
                  Bouaoun L, Sonkin D, Ardin M, Hollstein M, Byrnes G, Zavadil
                  J, Olivier M. TP53 Variations in Human Cancers: New Lessons
                  from the IARC TP53 Database and Genomics Data. Hum Mutat. 2016
                  Sep;37(9):865-76
                </p>
              </Citations>
            )}
            <Summary data={item} />
            <Charts data={item} />
          </div>
        </div>
      );
    });
  }

  return (
    <div className="varsome-result">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          title={title}
          version={version}
          url={url}
          helperPopup={<HelperText fieldDescriptionKey={title} />}
          content={tmpl}
        />
      </div>
    </div>
  );
};

IARCTP53Somatic.propTypes = {
  schema: PropTypes.shape({
    items: PropTypes.shape({
      properties: PropTypes.shape({}),
    }),
  }),
  values: PropTypes.arrayOf(PropTypes.shape({})),
  publications: PropTypes.shape({
    publications: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

IARCTP53Somatic.defaultProps = {
  schema: null,
  values: null,
  publications: {},
};

const mapStateToProps = (state) => ({
  schema: getTp53SomaticSchema(state),
  values: getTp53SomaticItems(state),
});

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(IARCTP53Somatic, "IARC TP53 Somatic", "IARC TP53 Somatic"));
